import { CanActivate, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Injectable, NgZone, Optional } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { from, map, MonoTypeOperatorFunction, Observable, of, take, switchMap, filter } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Auth, authState } from '@angular/fire/auth';
import { TwoFactorService } from '../services/two-factor.service';
// #813 — décision rôle/matrice déléguée à PermissionService (RBAC étape 2).
// PermissionService est providedIn:'root' ; pas de cycle d'import (le service
// ne dépend pas du guard).
import { PermissionService } from '../../backoffice/services/permission.service';
import { RbacModule } from '../../backoffice/models/rbac.models';

/**
 * #2031 — ramène chaque émission dans la zone Angular.
 *
 * Le profil employé arrive par `docData` appelé dans un `switchMap`, donc hors
 * contexte d'injection : AngularFire rend alors l'Observable BRUT de Firestore,
 * sans le retour dans la zone Angular. Au chargement à froid (F5, favori), la
 * première réponse vient d'un rappel réseau hors zone : le garde, le routeur, puis
 * la création de l'écran se faisaient hors zone. Tous les écouteurs de l'écran
 * étaient posés hors zone, et un clic (menu du compte) ne redessinait plus rien.
 */
function enterAngularZone<T>(zone: NgZone | null): MonoTypeOperatorFunction<T> {
  return source => !zone ? source : new Observable<T>(subscriber => source.subscribe({
    next: value => zone.run(() => subscriber.next(value)),
    error: err => zone.run(() => subscriber.error(err)),
    complete: () => zone.run(() => subscriber.complete()),
  }));
}

@Injectable({
  providedIn: 'root'
})
export class StaffAuthGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private router: Router,
    private auth: Auth,
    private twoFactorService: TwoFactorService,
    private permissionService: PermissionService,
    // #2031 — facultatif pour les specs qui construisent le garde à la main.
    @Optional() private ngZone: NgZone | null = null
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Observable<boolean> {
    const routeRoles = (route.data['roles'] as Array<string>) ?? [];
    const routePath = route.routeConfig?.path ?? '';
    const explicitRbac = (route.data['rbac'] as { module: RbacModule; subModule: string } | undefined) ?? null;
    // #1315 — la destination doit voyager AVEC le déroutement. Sans elle, les
    // écrans 2FA n'ont rien à honorer et retombent sur `/backoffice` : la perte
    // se produisait ici, en amont de l'écran accusé. `state.url` porte chemin +
    // query + fragment (même choix que `PortalAuthGuard` et `TeamOnlyGuard`).
    const returnUrl = state?.url || '/backoffice';

    // Utiliser afAuth.user qui émet null quand pas connecté, et le user quand connecté
    // Contrairement à authState, on attend que Firebase Auth soit initialisé
    return authState(this.auth).pipe(
      // Attendre que Firebase Auth soit initialisé (premier emit non-undefined)
      take(1),
      // #1993 — la configuration (`whenReady`) n'est PLUS lue ici : `app_config`
      // n'est lisible qu'avec la 2FA de la connexion en cours (#1975). La lire
      // avant ce contrôle échouait pour tout visiteur déconnecté ou pas encore
      // passé par le code — et l'échec restait en cache. Elle est lue plus bas,
      // une fois l'utilisateur et sa 2FA vérifiés.
      switchMap(user => {
        console.log('[StaffAuthGuard] authState user:', user?.email || 'null');

        if (!user) {
          console.warn('[StaffAuthGuard] Pas d\'utilisateur → /backoffice-login');
          // #2009 (A3) — la page demandée voyage avec le déroutement : après la
          // connexion et le code, la porte du personnel y ramène (au lieu de
          // l'accueil du back-office).
          this.router.navigate(['/backoffice-login'], { queryParams: { returnUrl } });
          return of(false);
        }

        const email = (user.email || '').toLowerCase();
        if (!email.endsWith('@befret.be')) {
          console.warn('[StaffAuthGuard] Email non @befret.be:', email);
          this.router.navigate(['/auth']);
          return of(false);
        }

        console.log('[StaffAuthGuard] Recherche profil employé pour:', email, 'uid:', user.uid);

        return this.authService.getEmployeeProfileByUid(user.uid).pipe(
          take(1),
          // #2031 — AVANT toute la suite : la décision, la navigation et la
          // création de l'écran doivent se faire dans la zone Angular.
          enterAngularZone(this.ngZone),
          // #867 étape 2 — la claim `mfaAt` du token prime sur sessionStorage
          // quand elle est là : une vérification vieille de plus de 12 h est
          // refusée par firestore.rules, l'UI doit le savoir avant d'ouvrir des
          // écrans qui n'afficheraient que des erreurs de permission.
          switchMap((employee: any) => from(this.twoFactorService.isMfaClaimFresh()).pipe(
            map((claimFresh) => ({ employee, claimFresh })))),
          switchMap(({ employee, claimFresh }: { employee: any; claimFresh: boolean | null }): Observable<boolean> => {
            console.log('[StaffAuthGuard] Profil trouvé:', employee);
            const employeeRole = employee?.role || employee?.ability;
            // #2073 — une fiche renseignée à un autre statut que « active »
            // (inactive, pending, archived…) n'ouvre plus le back-office. Statut
            // absent = active, même règle que `isEmployeeRecordActive` côté fonctions.
            const status = employee?.status;
            const isActive = status === undefined || status === null || status === '' || status === 'active';
            if (employee && !isActive) {
              console.warn('[StaffAuthGuard] Fiche employé non active:', status);
              this.router.navigate(['/backoffice-login']);
              return of(false);
            }
            if (!employee || !employeeRole) {
              console.warn('[StaffAuthGuard] Rôle insuffisant:', employeeRole, 'requis:', routeRoles);
              this.router.navigate(['/backoffice-login']);
              return of(false);
            }
            // Claim absente (null) ⇒ on ne tranche pas sur le token et on garde
            // le comportement historique : sessionStorage seul.
            const verified = claimFresh !== null
              ? claimFresh
              : this.twoFactorService.is2FAVerified();
            // 2FA AVANT la décision de rôle (#1993) : celle-ci lit la
            // configuration, illisible tant que le code n'est pas passé.
            if (employee.twoFactorEnabled && !verified) {
              console.warn('[StaffAuthGuard] 2FA requis mais non vérifié → /verify-2fa');
              this.twoFactorService.setPendingUid(user.uid);
              this.router.navigate(['/verify-2fa'], { queryParams: { returnUrl } });
              return of(false);
            }
            if (!employee.twoFactorEnabled && !verified) {
              console.warn('[StaffAuthGuard] 2FA non configuré → /setup-2fa (obligatoire)');
              this.twoFactorService.setPendingUid(user.uid);
              this.router.navigate(['/setup-2fa'], { queryParams: { returnUrl } });
              return of(false);
            }
            // #813 — flag + permissions matrice chargés avant de décider (attente
            // bornée à 5 s ; à défaut, routeAllowed retombe sur le legacy).
            // #813 — matrice RBAC quand le flag est actif et le module couvert,
            // sinon data.roles legacy (super_admin court-circuité dans les deux cas)
            return from(this.permissionService.whenReady()).pipe(map(() => {
              if (this.permissionService.routeAllowed(routePath, routeRoles, employeeRole, explicitRbac)) {
                console.log('[StaffAuthGuard] Accès autorisé, rôle:', employeeRole);
                return true;
              }
              console.warn('[StaffAuthGuard] Rôle insuffisant:', employeeRole, 'requis:', routeRoles);
              this.router.navigate(['/backoffice-login']);
              return false;
            }));
          }),
          catchError((err) => {
            console.error('[StaffAuthGuard] Erreur:', err);
            this.router.navigate(['/backoffice-login']);
            return of(false);
          })
        );
      })
    );
  }
}
